import { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/Auth';
import { useOrganization } from '../../utils/OrganizationContext';
import Avatar from '../Avatar';
import styles from './OrganizationSelector.module.css';

/**
 * OrganizationSelector - A dropdown component for switching between organizations
 * 
 * Usage:
 * import OrganizationSelector from './components/OrganizationSelector';
 * 
 * <OrganizationSelector />
 */
const OrganizationSelector = () => {
    const navigate = useNavigate();
    const {
        organizations,
        activeOrgId,
        switchOrganization,
        loadingOrgs,
        error
    } = useOrganization();
    const { currentUser } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Sort: personal first, then active (if different), then alphabetically
    const sortedOrganizations = useMemo(() => {
        let personal = null;
        let active = null;
        let others = [];

        organizations.forEach(org => {
            if (org.id === currentUser?.uid) {
                personal = org;
            } else if (org.id === activeOrgId) {
                active = org;
            } else {
                others.push(org);
            }
        });

        // Sort others alphabetically
        others.sort((a, b) => {
            const nameA = a.name || 'Unnamed Organization';
            const nameB = b.name || 'Unnamed Organization';
            return nameA.localeCompare(nameB);
        });

        // Build result: personal first, then active (if not personal), then others
        const result = [];
        if (personal) result.push(personal);
        if (active && active.id !== personal?.id) result.push(active);
        result.push(...others);

        return result;
    }, [organizations, activeOrgId, currentUser?.uid]);

    const handleOrgChange = (newOrgId) => {
        // Handle "Create Organization" option
        if (newOrgId === 'new') {
            navigate('/organizations/create');
            setIsOpen(false);
            return;
        }

        if (newOrgId && newOrgId !== activeOrgId) {
            switchOrganization(newOrgId);
        }
        setIsOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Don't render if user has no organizations or only one
    if (!organizations) {
        return null;
    }

    if (loadingOrgs) {
        return <div className={styles.loading}>Loading organizations...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error loading organizations</div>;
    }

    const activeOrg = sortedOrganizations.find(org => org.id === activeOrgId);
    const activeOrgName = activeOrg?.name || (activeOrg?.metadata?.type === 'personal' ? 'Personal Account' : 'Unnamed Organization');
    const activeOrgAvatar = activeOrg?.metadata?.avatar_image_url;

    return (
        <div className={styles.dropdown} ref={dropdownRef}>
            <button
                className={styles.dropdownToggle}
                onClick={() => setIsOpen(!isOpen)}
                disabled={loadingOrgs}
            >
                <div className={styles.selectedOrg}>
                    <Avatar 
                        src={activeOrgAvatar} 
                        orgName={activeOrgName} 
                        size="small"
                        alt={`${activeOrgName} avatar`}
                    />
                    <span className={styles.orgNameText}>
                        {activeOrgName}
                        {activeOrg?.metadata?.type === 'personal' && <span className={styles.badge}>(You)</span>}
                    </span>
                </div>
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} />
            </button>

            {isOpen && (
                <div className={styles.dropdownMenu}>
                    {sortedOrganizations.map(org => {
                        const isPersonal = org.id === currentUser?.uid;
                        const isActive = org.id === activeOrgId;
                        const orgName = org.name || (isPersonal ? 'Personal Account' : 'Unnamed Organization');
                        const avatarUrl = org?.metadata?.avatar_image_url;

                        return (
                            <button
                                key={org.id}
                                className={`${styles.dropdownItem} ${isActive ? styles.active : ''}`}
                                onClick={() => handleOrgChange(org.id)}
                                title={orgName}
                            >
                                <Avatar 
                                    src={avatarUrl} 
                                    orgName={orgName} 
                                    size="small"
                                    alt={`${orgName} avatar`}
                                />
                                <span className={styles.orgNameText}>
                                    {orgName}
                                    {isPersonal && <span className={styles.badge}>(You)</span>}
                                </span>
                            </button>
                        );
                    })}
                    <div className={styles.divider}></div>
                    <button
                        className={styles.dropdownItem}
                        onClick={() => handleOrgChange('new')}
                    >
                        <div className={styles.createIcon}>
                            <i className='fas fa-plus'></i>
                        </div>
                        <span className={styles.orgNameText}>Create Organization</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default OrganizationSelector;
